/**
 * DataExport settings panel.
 *
 * Lets the user request a full account export: account-data.json + mail.mbox
 * bundled in a ZIP. The server handles export assembly; this component drives
 * the request and reacts to the three possible outcomes:
 *
 *   1. ZIP streamed directly   → 200 application/zip  → browser download
 *   2. Too large, emailed out  → 202 JSON             → success message with address
 *   3. Too large, no bucket    → 503 JSON             → error asking user to contact support
 *
 * Rate limit is 2 requests per hour; 429 is surfaced as a friendly message.
 *
 * The delivery email field is pre-filled with the user's own address because
 * that's the overwhelmingly common case. The field is editable so the user can
 * redirect to a different inbox without changing their account email.
 */

import React, { useState, useEffect } from "react";
import { apiGet } from "../../api";
import { t, type Locale } from "../../i18n";

// ─── Types ───────────────────────────────────────────────────────────────────

interface MeResponse {
  email: string;
  recoverySetup: boolean;
}

/**
 * Discriminated union returned by downloadExport so the component can switch
 * cleanly on the outcome without inspecting raw HTTP details.
 */
type ExportResult =
  | { kind: "downloaded" }
  | { kind: "emailed"; to: string }
  | { kind: "error"; message: string };

// ─── API helper ──────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

/**
 * POST /v1/export — must use raw fetch (not apiPost) because the success
 * response may be a binary ZIP, not JSON. apiPost always calls readJsonSafe
 * which would corrupt binary data.
 *
 * Content-type inspection logic:
 *   - res.headers.get("content-type")?.includes("application/zip")
 *     → read as Blob, synthesize <a download>, revoke object URL after click
 *   - res.status === 202 and JSON body has status === "emailed"
 *     → return { kind: "emailed", to }
 *   - res.status === 503 → specific "not configured" error
 *   - res.status === 429 → rate-limit message
 *   - other non-OK → generic error from JSON body if present
 */
export async function downloadExport(deliveryEmail: string): Promise<ExportResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/export`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      // deliveryEmail is always sent; the server ignores it for small exports
      // and uses it as the link-delivery address for large ones.
      body: JSON.stringify({ deliveryEmail }),
    });
  } catch {
    // Network-level failure (offline, DNS, etc.)
    return { kind: "error", message: "Network error — check your connection and try again." };
  }

  const contentType = res.headers.get("content-type") ?? "";

  // ── Happy path: server streamed a ZIP ────────────────────────────────────
  if (res.ok && contentType.includes("application/zip")) {
    let blob: Blob;
    try {
      blob = await res.blob();
    } catch {
      return { kind: "error", message: "Failed to read the export file from the server." };
    }

    // Derive filename from Content-Disposition if the server set one,
    // otherwise fall back to a timestamped default.
    const disposition = res.headers.get("content-disposition") ?? "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1]?.trim() || `kveja-export-${Date.now()}.zip`;

    // Synthesize a temporary object URL and click it programmatically.
    // The URL is revoked immediately after the click so it doesn't linger
    // in the browser's blob registry — blobs can be large.
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Revoke on the next tick — the browser needs one event loop turn to
    // register the download before the URL is invalidated.
    setTimeout(() => URL.revokeObjectURL(url), 100);

    return { kind: "downloaded" };
  }

  // ── 202: export was too large, emailed instead ───────────────────────────
  if (res.status === 202) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return { kind: "error", message: "Unexpected response from server." };
    }
    const to = (body as Record<string, unknown>)?.to;
    return { kind: "emailed", to: typeof to === "string" ? to : deliveryEmail };
  }

  // ── 429: rate limited ────────────────────────────────────────────────────
  if (res.status === 429) {
    return { kind: "error", message: "429" }; // component maps this to the i18n string
  }

  // ── 503: bucket not configured ───────────────────────────────────────────
  if (res.status === 503) {
    return { kind: "error", message: "503" }; // component maps this to the i18n string
  }

  // ── Other non-OK (400, 500, etc.) ────────────────────────────────────────
  let errorMessage: string | null = null;
  try {
    const body = await res.json() as Record<string, unknown>;
    if (typeof body?.error === "string") errorMessage = body.error;
    else if (typeof body?.message === "string") errorMessage = body.message;
  } catch {
    // body not JSON — fall through to generic message
  }
  return {
    kind: "error",
    message: errorMessage ?? `Unexpected error (HTTP ${res.status}). Please try again.`,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DataExport(props: { locale: Locale }) {
  const { locale } = props;

  const [userEmail, setUserEmail] = useState<string>("");
  const [deliveryEmail, setDeliveryEmail] = useState<string>("");
  const [loadingUser, setLoadingUser] = useState(true);

  const [exporting, setExporting] = useState(false);
  // null = idle; "downloaded" | "emailed:<address>" = success; string = error message
  const [successKind, setSuccessKind] = useState<"downloaded" | null>(null);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pre-fill the delivery email with the user's own address so the common
  // case (export to yourself) requires zero typing.
  useEffect(() => {
    void (async () => {
      const res = await apiGet<MeResponse>("/v1/me");
      setLoadingUser(false);
      if (res.ok) {
        setUserEmail(res.data.email);
        setDeliveryEmail(res.data.email);
      }
    })();
  }, []);

  async function handleExport() {
    setExporting(true);
    setSuccessKind(null);
    setEmailedTo(null);
    setErrorMsg(null);

    const result = await downloadExport(deliveryEmail);

    setExporting(false);

    if (result.kind === "downloaded") {
      setSuccessKind("downloaded");
    } else if (result.kind === "emailed") {
      setEmailedTo(result.to);
    } else {
      // Map the sentinel error codes to localised strings
      if (result.message === "429") {
        setErrorMsg(t(locale, "dataExport.error.rateLimited"));
      } else if (result.message === "503") {
        setErrorMsg(t(locale, "dataExport.error.bucketNotConfigured"));
      } else {
        setErrorMsg(result.message);
      }
    }
  }

  // ─── Styles (matching ExternalClient.tsx conventions) ─────────────────────

  const cardStyle: React.CSSProperties = {
    background: "var(--canvas)",
    border: "1px solid var(--line)",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
  };

  const errorBoxStyle: React.CSSProperties = {
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "6px",
    padding: "12px",
    marginTop: "16px",
    color: "#ef4444",
    fontSize: "13px",
    lineHeight: "1.5",
  };

  const successBoxStyle: React.CSSProperties = {
    background: "rgba(34, 197, 94, 0.08)",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    borderRadius: "6px",
    padding: "12px",
    marginTop: "16px",
    fontSize: "13px",
    color: "var(--text)",
    lineHeight: "1.5",
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>
        {t(locale, "settings.advanced.title")}
      </h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>
        {t(locale, "dataExport.pageDescription")}
      </p>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "8px" }}>
          {t(locale, "dataExport.card.title")}
        </h3>

        <p style={{ fontSize: "13px", color: "var(--muted)", marginTop: 0, marginBottom: "16px" }}>
          {t(locale, "dataExport.card.description")}
        </p>

        {/* Delivery email field */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="data-export-email"
            style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}
          >
            {t(locale, "dataExport.field.emailLabel")}
          </label>
          <input
            id="data-export-email"
            type="email"
            value={deliveryEmail}
            onChange={(e) => {
              setDeliveryEmail(e.target.value);
              // Clear any stale result when the user edits the address
              setSuccessKind(null);
              setEmailedTo(null);
              setErrorMsg(null);
            }}
            placeholder={userEmail || "you@example.com"}
            disabled={loadingUser || exporting}
            style={{
              width: "320px",
              maxWidth: "100%",
              padding: "8px",
              border: "1px solid var(--control-border)",
              borderRadius: "4px",
              fontSize: "13px",
              boxSizing: "border-box",
              opacity: loadingUser ? 0.6 : 1,
            }}
          />
          <p style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px", marginBottom: 0 }}>
            {t(locale, "dataExport.field.emailHint")}
          </p>
        </div>

        {/* Export button */}
        <button
          onClick={() => void handleExport()}
          disabled={exporting || loadingUser}
          style={{
            background: "var(--accent)",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: exporting || loadingUser ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: 600,
            opacity: exporting || loadingUser ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!exporting && !loadingUser) e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = exporting || loadingUser ? "0.6" : "1";
          }}
        >
          {exporting
            ? t(locale, "dataExport.button.exporting")
            : t(locale, "dataExport.button.export")}
        </button>

        {/* Success: ZIP downloaded directly */}
        {successKind === "downloaded" && (
          <div style={successBoxStyle}>
            {t(locale, "dataExport.success.downloaded")}
          </div>
        )}

        {/* Success: export emailed as a download link */}
        {emailedTo !== null && (
          <div style={successBoxStyle}>
            {t(locale, "dataExport.success.emailed", { to: emailedTo })}
          </div>
        )}

        {/* Error */}
        {errorMsg !== null && (
          <div style={errorBoxStyle}>
            {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
}
